import { useGetCoreProductGendersInfiniteQuery } from '@autone/redux/core';
import { sentenceCase } from '@autone/utils';
import { forwardRef } from 'react';

import { VirtualMultiselect } from '../../virtual-multiselect';
import { CoreGeneralFilter } from '../components/CoreGeneralFilter';
import { SERVER_SIDE_SEARCH_LIMIT } from '../constants';
import type {
  CoreGeneralFilterProps,
  CoreGeneralFilterRef,
} from '../CoreGeneralFilters.types';
import { useCoreProductGeneralFiltersContext } from '../hooks/useCoreProductGeneralFiltersContext';

export const CoreProductGenderFilter = forwardRef<
  CoreGeneralFilterRef,
  CoreGeneralFilterProps
>(({ alwaysCommit, searchTerm, className }, ref) => {
  const {
    dimensionAttributeValue,
    setDimensionAttributeValue,
    coreProductFiltersQueryArg,
  } = useCoreProductGeneralFiltersContext('product.genders');

  const { items, isFetching, isFetchingNextPage, fetchNextPage } =
    useGetCoreProductGendersInfiniteQuery(
      {
        query: {
          limit: SERVER_SIDE_SEARCH_LIMIT,
          search_term: searchTerm,
        },
        body: {
          filters: {
            product: coreProductFiltersQueryArg.product ?? {},
            catalogue: coreProductFiltersQueryArg.catalogue ?? {},
            product_groups: coreProductFiltersQueryArg.product_groups ?? {},
            location_cluster: coreProductFiltersQueryArg.location_cluster ?? {},
            size_run: coreProductFiltersQueryArg.size_run ?? {},
            size: coreProductFiltersQueryArg.size ?? {},
          },
        },
      },
      {
        selectFromResult: ({ data, ...rest }) => {
          return {
            ...rest,
            items: data?.pages.flatMap((page) => page.items) ?? [],
          };
        },
      },
    );

  return (
    <CoreGeneralFilter
      isFetching={isFetching && !isFetchingNextPage}
      onScrollEnd={fetchNextPage}
      ref={ref}
      selectedItems={dimensionAttributeValue}
      onSelectedItemsChange={setDimensionAttributeValue}
      alwaysCommit={alwaysCommit}
      className={className}
      items={items}
      renderItem={(item) => (
        <VirtualMultiselect.Item>
          <VirtualMultiselect.Checkbox />
          {sentenceCase(item.payload.description)}
        </VirtualMultiselect.Item>
      )}
    />
  );
});

CoreProductGenderFilter.displayName = 'CoreProductGenderFilter';
